import {
  isAtlasAgentMessage,
  type AtlasAgentMessage,
  type AtlasGestureStep,
  type AtlasParentMessage,
  type AtlasPin,
  type AtlasReplayResultMessage,
  type AtlasViewerMode,
} from '@atlas/api-contract';

/**
 * The parent half of the agent `postMessage` protocol.
 *
 * ============================================================================
 * READ THIS BEFORE CHANGING ANY ORIGIN CHECK IN THIS FILE
 * ============================================================================
 * The prototype is framed with `sandbox="allow-scripts allow-forms
 * allow-popups"` and deliberately WITHOUT `allow-same-origin`. That gives the
 * framed document an **opaque origin**, and an opaque origin changes both
 * directions of this channel in ways that are easy to "harden" into silence.
 *
 * Verified empirically in Chrome 149 against a cross-origin sandboxed frame:
 *
 *   inbound  event.origin ....................... "null"  (the literal string)
 *            event.origin === SANDBOX_ORIGIN .... false
 *            event.source === iframe.contentWindow  true
 *   outbound postMessage(msg, '*') ............. delivered
 *            postMessage(msg, SANDBOX_ORIGIN) ... SILENTLY DROPPED, no throw
 *   probe    iframe.contentDocument ............. null
 *
 * Two consequences, and both are the opposite of the obvious implementation:
 *
 * 1. **Inbound messages cannot be authenticated by origin.** `event.origin` is
 *    the string `"null"` for every message the agent sends, so comparing it
 *    with `VITE_SANDBOX_ORIGIN` rejects every real message and accepts nothing.
 *    Worse, `"null"` is also what a *different* sandboxed frame or a
 *    `data:`/`blob:` document reports, so the value carries no information at
 *    all and must not be used as a credential. The check that does work is
 *    **`event.source === iframe.contentWindow`**: a `WindowProxy` identity that
 *    nothing outside our own frame tree can forge. That plus the contract's
 *    shape guard is the whole authentication story, and it is enough.
 *
 * 2. **Outbound messages must use `targetOrigin: '*'`.** An opaque origin has
 *    no name, so there is no string that could be passed instead — naming the
 *    sandbox origin makes the browser drop the message with no error, and the
 *    viewer would simply stop responding with nothing in the console to explain
 *    it. This is safe here for two independent reasons: the agent validates
 *    that *its* `event.origin` equals the parent origin it was configured with
 *    (the file server injects it as `?parentOrigin=…`), and nothing sent over
 *    this channel is secret — modes, screen paths and already-public pin
 *    geometry. Never put a token, a session id or anything user-private in an
 *    `AtlasParentMessage`.
 *
 * Everything in this module is framework-free so both rules can be tested
 * without a DOM.
 */

/**
 * `'*'`, and it must stay `'*'`. See the block comment above: the framed
 * document's origin is opaque and cannot be named.
 */
export const AGENT_TARGET_ORIGIN = '*';

/**
 * How long a gesture replay may run before the parent gives up on it. The agent
 * fails closed on any step it cannot dispatch, so a request that has neither
 * resolved nor errored by now is a lost or wedged one; the promise rejects so
 * selecting the thread can fall back rather than hang on the pin forever.
 */
export const REPLAY_TIMEOUT_MS = 5_000;

/** The part of a `MessageEvent` the bridge actually inspects. */
export interface AgentMessageEventLike {
  readonly source: unknown;
  readonly data: unknown;
  /**
   * Present so tests and logs can record it. Deliberately never compared
   * against anything — it is `"null"` for every message the agent sends.
   */
  readonly origin?: string | undefined;
}

/** Why a message was refused, for tests and for a developer-facing log. */
export type AgentMessageRejection =
  /** No iframe is mounted, so nothing can legitimately be talking to us. */
  | 'no-agent-window'
  /** Some other window sent this. The overwhelmingly common case in the wild. */
  | 'foreign-source'
  /** Right window, but not a shape the contract defines. */
  | 'unknown-shape';

export type AgentMessageVerdict =
  | { readonly accepted: true; readonly message: AtlasAgentMessage }
  | { readonly accepted: false; readonly reason: AgentMessageRejection };

/**
 * Decide whether one `message` event really came from our agent.
 *
 * Ordered so the cheapest and most discriminating check runs first: on a page
 * with third-party widgets, extensions and dev-server sockets, `source`
 * identity rejects almost everything before any payload is touched.
 */
export const verifyAgentMessage = (
  event: AgentMessageEventLike,
  agentWindow: unknown,
): AgentMessageVerdict => {
  if (agentWindow === null || agentWindow === undefined) {
    return { accepted: false, reason: 'no-agent-window' };
  }

  if (event.source !== agentWindow) {
    return { accepted: false, reason: 'foreign-source' };
  }

  if (!isAtlasAgentMessage(event.data)) {
    return { accepted: false, reason: 'unknown-shape' };
  }

  return { accepted: true, message: event.data };
};

/** How a message reaches the agent. Injected so the bridge can be tested. */
export type AgentPostMessage = (
  agentWindow: unknown,
  message: AtlasParentMessage,
  targetOrigin: string,
) => void;

/** The real one: `Window.postMessage`, always with `'*'`. */
export const postToAgentWindow: AgentPostMessage = (
  agentWindow,
  message,
  targetOrigin,
) => {
  (agentWindow as Window).postMessage(message, targetOrigin);
};

export interface AgentBridgeOptions {
  /**
   * The window currently hosting the agent, resolved fresh on every call.
   *
   * A getter rather than a value because the iframe's `contentWindow` is
   * replaced whenever the frame reloads or its `src` changes, and a bridge
   * holding the old one would post into a window that no longer exists.
   */
  readonly getAgentWindow: () => unknown;
  /** Defaults to {@link postToAgentWindow}. */
  readonly post?: AgentPostMessage;
  /** Called for every accepted message, after the bridge's own bookkeeping. */
  readonly onMessage?: (message: AtlasAgentMessage) => void;
  /** Called whenever readiness flips, so React can re-render. */
  readonly onReadyChange?: (ready: boolean) => void;
}

export interface AgentBridge {
  /** Feed a raw `message` event in. Returns what was decided and why. */
  receive: (event: AgentMessageEventLike) => AgentMessageVerdict;
  /**
   * Send a message, or queue it if the agent has not announced itself yet.
   * Returns `true` when it went out, `false` when it was queued.
   */
  send: (message: AtlasParentMessage) => boolean;
  /**
   * The document is being replaced (a reload, a version switch, a link click
   * inside the prototype). Readiness drops and the queue is discarded.
   */
  reset: () => void;
  isReady: () => boolean;
  /** Queued messages waiting for `atlas:ready`. For tests and diagnostics. */
  queued: () => readonly AtlasParentMessage[];

  setMode: (mode: AtlasViewerMode) => boolean;
  navigate: (screenPath: string) => boolean;
  renderPins: (pins: readonly AtlasPin[], showResolved: boolean) => boolean;
  highlightPin: (threadId: string | null) => boolean;
  scrollToPin: (threadId: string) => boolean;
  /**
   * Tell the agent the parent has refreshed the prototype's signed access, so it
   * may retry the assets that failed with `401` (spec R7.6).
   */
  notifyAccessRefreshed: () => boolean;
  /**
   * Ask the agent to replay a thread's recorded gesture path and drive the
   * prototype back to the state the pin was placed in. Resolves with the
   * agent's `atlas:replay-result` (its `ok` says whether the expected screen was
   * reached), and rejects on timeout ({@link REPLAY_TIMEOUT_MS}) or when the
   * document is replaced mid-replay.
   */
  replay: (
    threadId: string,
    gesturePath: readonly AtlasGestureStep[],
    expectedScreenKey: string | null,
  ) => Promise<AtlasReplayResultMessage>;
}

/**
 * Why messages are queued rather than dropped:
 *
 * The viewer knows what mode it is in and which screen it wants before the
 * agent has finished booting, and a reviewer who flips comment mode on during
 * the first second of a load must not have to flip it twice. Queuing makes the
 * senders safe to call at any time; the alternative is every caller owning a
 * "wait for ready" dance.
 *
 * The queue is keyed by message `type`, so a burst of mode toggles collapses to
 * the last one. That is not an optimisation — it is correctness. Replaying
 * three stale `atlas:set-mode` messages in order would leave the agent in
 * whichever mode the *first* click asked for.
 */
export const createAgentBridge = (options: AgentBridgeOptions): AgentBridge => {
  const post = options.post ?? postToAgentWindow;

  let ready = false;
  const queue = new Map<AtlasParentMessage['type'], AtlasParentMessage>();

  /**
   * Replays in flight, keyed by thread id. Each holds the promise's settlers and
   * the timeout guarding it. Keyed by thread so two threads can be restored
   * without their results being confused — and so a second replay of the *same*
   * thread supersedes the first rather than leaking it.
   */
  interface PendingReplay {
    readonly resolve: (result: AtlasReplayResultMessage) => void;
    readonly reject: (error: Error) => void;
    readonly timer: ReturnType<typeof setTimeout>;
  }
  const pendingReplays = new Map<string, PendingReplay>();

  const settleReplay = (threadId: string): PendingReplay | undefined => {
    const pending = pendingReplays.get(threadId);

    if (pending !== undefined) {
      clearTimeout(pending.timer);
      pendingReplays.delete(threadId);
    }

    return pending;
  };

  const setReady = (next: boolean): void => {
    if (ready === next) {
      return;
    }

    ready = next;
    options.onReadyChange?.(next);
  };

  const deliver = (message: AtlasParentMessage): boolean => {
    const agentWindow = options.getAgentWindow();

    if (agentWindow === null || agentWindow === undefined) {
      return false;
    }

    post(agentWindow, message, AGENT_TARGET_ORIGIN);
    return true;
  };

  const flush = (): void => {
    const pending = [...queue.values()];
    queue.clear();

    for (const message of pending) {
      if (!deliver(message)) {
        // The window vanished mid-flush. Put the rest back rather than losing
        // it; the next `atlas:ready` will try again.
        queue.set(message.type, message);
      }
    }
  };

  const send = (message: AtlasParentMessage): boolean => {
    if (!ready || !deliver(message)) {
      queue.set(message.type, message);
      return false;
    }

    return true;
  };

  return {
    receive: (event) => {
      const verdict = verifyAgentMessage(event, options.getAgentWindow());

      if (!verdict.accepted) {
        return verdict;
      }

      if (verdict.message.type === 'atlas:ready') {
        setReady(true);
        flush();
      }

      if (verdict.message.type === 'atlas:replay-result') {
        settleReplay(verdict.message.threadId)?.resolve(verdict.message);
      }

      options.onMessage?.(verdict.message);

      return verdict;
    },

    send,

    reset: () => {
      /*
       * The queue is cleared, not replayed. Everything in it described the
       * document that is going away — a pin set, a scroll target, a screen to
       * navigate to — and re-sending it to a freshly booted agent would tell it
       * about a page it is not on. The viewer re-establishes mode and pins from
       * its own state when the new agent says `atlas:ready`.
       */
      queue.clear();

      /*
       * A replay describes the document that is going away; its result can never
       * arrive now. Reject rather than leave the caller's promise to time out, so
       * the selection flow falls back at once instead of five seconds later.
       */
      for (const threadId of [...pendingReplays.keys()]) {
        settleReplay(threadId)?.reject(
          new Error('atlas:replay abandoned: the document was replaced'),
        );
      }

      setReady(false);
    },

    isReady: () => ready,
    queued: () => [...queue.values()],

    setMode: (mode) => send({ type: 'atlas:set-mode', mode }),
    navigate: (screenPath) => send({ type: 'atlas:navigate', screenPath }),
    renderPins: (pins, showResolved) =>
      send({ type: 'atlas:render-pins', pins: [...pins], showResolved }),
    highlightPin: (threadId) => send({ type: 'atlas:highlight-pin', threadId }),
    scrollToPin: (threadId) => send({ type: 'atlas:scroll-to-pin', threadId }),
    notifyAccessRefreshed: () =>
      send({ type: 'atlas:prototype-access-refreshed' }),

    replay: (threadId, gesturePath, expectedScreenKey) => {
      /*
       * A fresh request supersedes any in flight for the same thread — the
       * reviewer clicked the row again, and the old promise's owner has moved on.
       */
      settleReplay(threadId)?.reject(
        new Error('atlas:replay superseded by a newer request'),
      );

      return new Promise<AtlasReplayResultMessage>((resolve, reject) => {
        const timer = setTimeout(() => {
          pendingReplays.delete(threadId);
          reject(new Error('atlas:replay timed out'));
        }, REPLAY_TIMEOUT_MS);

        pendingReplays.set(threadId, { resolve, reject, timer });

        /*
         * Queued like any other sender when the agent is still booting; the
         * timeout covers a replay that is waiting on `atlas:ready` as much as one
         * the agent is actively running.
         */
        send({
          type: 'atlas:replay',
          threadId,
          gesturePath: [...gesturePath],
          expectedScreenKey,
        });
      });
    },
  };
};
